import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetLoanApplicationByTask, useUploadDocument } from '../../../services/loanService';
import type { UploadDocumentBodyDocumentType, Error } from '../../../services/schemas/loanServiceSchemas';
import './DocumentUpload.css';

const DocumentUpload: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<UploadDocumentBodyDocumentType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch loan application by task ID
  const {
    data: loanApplication,
    isLoading: isLoadingApplication,
    error: applicationError
  } = useGetLoanApplicationByTask(taskId || '', {
    query: {
      enabled: !!taskId
    }
  });

  // Upload document mutation
  const uploadDocumentMutation = useUploadDocument();

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      validateAndSetFile(selectedFile);
    }
  }, []);

  const validateAndSetFile = useCallback((selectedFile: File) => {
    // Check file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error(`Invalid file type: ${selectedFile.name}. Please upload PDF, JPG, PNG, DOC, or DOCX files.`);
      return;
    }

    // Check file size (100MB max)
    if (selectedFile.size > 100 * 1024 * 1024) {
      toast.error(`File size exceeds 100MB limit: ${selectedFile.name}`);
      return;
    }

    setFile(selectedFile);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  }, [validateAndSetFile]);

  const handleUpload = async () => {
    if (!selectedType || !file || !loanApplication?.id) {
      toast.error('Please select a document type and file');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev === null) return 0;
          if (prev >= 95) {
            clearInterval(interval);
            return 95;
          }
          return prev + 5;
        });
      }, 200);

      await uploadDocumentMutation.mutateAsync({
        loanApplicationId: loanApplication.id,
        data: {
          file,
          documentType: selectedType
        }
      });

      clearInterval(interval);
      setUploadProgress(100);
      toast.success('Document uploaded successfully');
      navigate(`/document-management/${taskId}`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  if (isLoadingApplication) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (applicationError) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Error loading loan application: {(applicationError as Error)?.message || 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="document-upload">
      <div className="page-header mb-4">
        <h4 className="mb-0">
          <i className="bi bi-file-earmark-arrow-up me-2 text-primary"></i>
          Upload Document
        </h4>
        <p className="text-muted mb-0">
          Upload a document for loan application #{loanApplication?.id}
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-4">
            <label className="form-label">Document Type</label>
            <select
              className="form-select"
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value as UploadDocumentBodyDocumentType)}
              disabled={isUploading}
            >
              <option value="">Select document type</option>
              <option value="ID_DOCUMENT">ID Document</option>
              <option value="PROOF_OF_ADDRESS">Proof of Address</option>
              <option value="BANK_STATEMENT">Bank Statement</option>
              <option value="TAX_CLEARANCE">Tax Clearance</option>
              <option value="FINANCIAL_STATEMENTS">Financial Statements</option>
              <option value="COMPANY_REGISTRATION_CERTIFICATE">Company Registration Certificate</option>
              <option value="PAYSLIP">Payslip</option>
              <option value="COLLATERAL_DOCUMENT">Collateral Document</option>
              <option value="LOAN_AGREEMENT">Loan Agreement</option>
              <option value="OTHER">Other Document</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="form-label">Upload File</label>
            <div
              className={`upload-area ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                className="d-none"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <div className="upload-content">
                {file ? (
                  <>
                    <i className="bi bi-file-earmark-text fs-1 text-primary mb-3"></i>
                    <p className="mb-0">{file.name}</p>
                    <small className="text-muted">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </small>
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload fs-1 text-primary mb-3"></i>
                    <p className="mb-0">Drag and drop a file here</p>
                    <small className="text-muted">or</small>
                    <label className="btn btn-outline-primary mt-2">
                      Browse Files
                      <input
                        type="file"
                        className="d-none"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={handleFileChange}
                        disabled={isUploading}
                      />
                    </label>
                    <p className="text-muted mt-2 mb-0">
                      Supported formats: PDF, JPG, PNG, DOC, DOCX (max 100MB)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {uploadProgress !== null && (
            <div className="mb-4">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(`/document-management/${taskId}`)}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!selectedType || !file || isUploading}
            >
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  Upload Document
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload; 